import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import type { WireArticleBlock, WireImageArtifact, WirePhotoAspectRatio, WirePhotoItem } from '../../api/types'
import { PhotoItemEditor } from './PhotoGroupBlockPanel'
import { CmtWrapper } from '../common/cmt'
import type { CmtResult } from '../common/cmt'

type ComparisonBlock = Extract<WireArticleBlock, { type: 'photo-comparison' }>

// ─── Option tables ─────────────────────────────────────────────────────────────

const WIDTH_OPTIONS: { value: NonNullable<ComparisonBlock['width']>; label: string }[] = [
  { value: 'column', label: 'Column' },
  { value: 'wide',   label: 'Wide'   },
  { value: 'full',   label: 'Full'   },
]

const RATIO_OPTIONS: { value: WirePhotoAspectRatio; label: string }[] = [
  { value: 'natural', label: 'Natural' },
  { value: '1:1',     label: '1:1'     },
  { value: '4:3',     label: '4:3'     },
  { value: '3:2',     label: '3:2'     },
  { value: '16:9',    label: '16:9'    },
  { value: '2:1',     label: '2:1'     },
  { value: '3:4',     label: '3:4'     },
  { value: '2:3',     label: '2:3'     },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
      {children}
    </p>
  )
}

// ─── Artifact picker ──────────────────────────────────────────────────────────

function ArtifactPicker({
  artifacts,
  onSelect,
  onClose,
}: {
  artifacts: WireImageArtifact[]
  onSelect: (a: WireImageArtifact) => void
  onClose: () => void
}) {
  return (
    <div className="rounded border border-line bg-canvas">
      <div className="flex items-center justify-between border-b border-line px-2 py-1.5">
        <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
          Change from artifacts
        </p>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-0.5 text-fg-secondary hover:bg-fill hover:text-fg"
          aria-label="Close picker"
        >
          <X size={12} />
        </button>
      </div>
      {artifacts.length === 0 ? (
        <p className="px-2 py-3 text-[11px] text-fg-secondary">
          No image artifacts uploaded yet.
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-1.5 p-2">
          {artifacts.map(a => (
            <button
              key={a.id}
              type="button"
              onClick={() => onSelect(a)}
              className="overflow-hidden rounded border border-line bg-fill transition-colors hover:border-accent-primary-solid"
              title={a.filename}
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={a.presignedUrl ?? ''}
                  alt={a.altText ?? ''}
                  className="h-full w-full object-cover"
                />
              </div>
              <p className="truncate px-1 py-0.5 text-[9px] text-fg-secondary">{a.filename}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Photo slot ───────────────────────────────────────────────────────────────
// Wraps ArtifactPicker + PhotoItemEditor for one Before/After slot.

function PhotoSlot({
  label,
  photo,
  imageUrl,
  imageArtifacts,
  onChange,
}: {
  label: string
  photo: WirePhotoItem
  imageUrl: string
  imageArtifacts: WireImageArtifact[]
  onChange: (updated: WirePhotoItem) => void
}) {
  const [pickerOpen, setPickerOpen] = useState(false)

  function selectArtifact(artifact: WireImageArtifact) {
    // Swap the image source while preserving the photo's stable id
    onChange({
      id: photo.id,
      artifactId: artifact.id,
      classification: artifact.classification,
      captionClassification: artifact.captionClassification,
      captionDisseminationControls: artifact.captionDisseminationControls,
      caption: artifact.caption,
      altText: artifact.altText,
      credit: artifact.credit,
      focalPoint: artifact.focalPoint,
      expandable: false,
    })
    setPickerOpen(false)
  }

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <FieldLabel>{label}</FieldLabel>
        <button
          type="button"
          onClick={() => setPickerOpen(o => !o)}
          className="rounded border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
        >
          Change
        </button>
      </div>
      {pickerOpen && (
        <div className="mb-2">
          <ArtifactPicker
            artifacts={imageArtifacts}
            onSelect={selectArtifact}
            onClose={() => setPickerOpen(false)}
          />
        </div>
      )}
      <PhotoItemEditor
        photo={photo}
        index={0}
        imageUrl={imageUrl}
        onChange={onChange}
        // onRemove intentionally omitted — slots are required; use Change to swap
      />
    </div>
  )
}

// ─── Main panel ───────────────────────────────────────────────────────────────

interface Props {
  block: ComparisonBlock
  onUpdate: (updated: ComparisonBlock) => void
  imageArtifacts: WireImageArtifact[]
}

export function PhotoComparisonBlockPanel({ block, onUpdate, imageArtifacts }: Props) {

  const [caption,    setCaption]    = useState(block.caption ?? '')
  const [captionCmt, setCaptionCmt] = useState<CmtResult | undefined>(
    block.captionClassification ? { classification: block.captionClassification, disseminationControls: block.captionDisseminationControls ?? [] } : undefined
  )
  const [beforeLabel, setBeforeLabel] = useState(block.beforeLabel ?? '')
  const [afterLabel,  setAfterLabel]  = useState(block.afterLabel  ?? '')

  // Sync if the block is replaced externally (e.g. after save/reload)
  useEffect(() => {
    setCaption(block.caption ?? '')
    setCaptionCmt(block.captionClassification ? { classification: block.captionClassification, disseminationControls: block.captionDisseminationControls ?? [] } : undefined)
    setBeforeLabel(block.beforeLabel ?? '')
    setAfterLabel(block.afterLabel  ?? '')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id])

  function patchBlock(patch: Partial<ComparisonBlock>) {
    onUpdate({ ...block, ...patch })
  }

  function commitCaption() {
    const val = caption.trim() || undefined
    if (val !== block.caption) patchBlock({ caption: val })
  }

  function commitCaptionCmt(result: CmtResult) {
    setCaptionCmt(result)
    patchBlock({ captionClassification: result.classification, captionDisseminationControls: result.disseminationControls.length ? result.disseminationControls : undefined })
  }

  function commitBeforeLabel() {
    const val = beforeLabel.trim() || undefined
    if (val !== block.beforeLabel) patchBlock({ beforeLabel: val })
  }

  function commitAfterLabel() {
    const val = afterLabel.trim() || undefined
    if (val !== block.afterLabel) patchBlock({ afterLabel: val })
  }

  const width       = block.width       ?? 'column'
  const aspectRatio = block.aspectRatio ?? 'natural'
  const sliderPos   = block.initialPosition ?? 0.5

  return (
    <div className="space-y-4">

      {/* ── Before photo ── */}
      <PhotoSlot
        label="Before Photo"
        photo={block.before}
        imageUrl={imageArtifacts.find(a => a.id === block.before.artifactId)?.presignedUrl ?? ''}
        imageArtifacts={imageArtifacts}
        onChange={updated => patchBlock({ before: updated })}
      />

      {/* ── After photo ── */}
      <PhotoSlot
        label="After Photo"
        photo={block.after}
        imageUrl={imageArtifacts.find(a => a.id === block.after.artifactId)?.presignedUrl ?? ''}
        imageArtifacts={imageArtifacts}
        onChange={updated => patchBlock({ after: updated })}
      />

      {/* ── Overlay labels ── */}
      <div>
        <FieldLabel>Labels</FieldLabel>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="mb-0.5 text-[9px] text-fg-secondary">Before</p>
            <input
              type="text"
              value={beforeLabel}
              onChange={e => setBeforeLabel(e.target.value)}
              onBlur={commitBeforeLabel}
              onKeyDown={e => { if (e.key === 'Enter') commitBeforeLabel() }}
              placeholder="Before"
              className="w-full rounded border border-line bg-fill px-1.5 py-1 text-xs text-fg placeholder:text-fg-secondary focus:border-accent-primary-line focus:outline-none"
            />
          </div>
          <div>
            <p className="mb-0.5 text-[9px] text-fg-secondary">After</p>
            <input
              type="text"
              value={afterLabel}
              onChange={e => setAfterLabel(e.target.value)}
              onBlur={commitAfterLabel}
              onKeyDown={e => { if (e.key === 'Enter') commitAfterLabel() }}
              placeholder="After"
              className="w-full rounded border border-line bg-fill px-1.5 py-1 text-xs text-fg placeholder:text-fg-secondary focus:border-accent-primary-line focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* ── Shared caption ── */}
      <div>
        <FieldLabel>Shared Caption</FieldLabel>
        <textarea
          value={caption}
          onChange={e => setCaption(e.target.value)}
          onBlur={commitCaption}
          rows={2}
          placeholder="Caption text…"
          className="w-full resize-none rounded border border-line bg-fill px-1.5 py-1 text-xs text-fg placeholder:text-fg-secondary focus:border-accent-primary-line focus:outline-none"
        />
      </div>

      {/* ── Caption portion mark ── */}
      <div>
        <FieldLabel>Caption Portion Mark</FieldLabel>
        <CmtWrapper value={captionCmt} onChange={commitCaptionCmt} compact />
      </div>

      {/* ── Initial slider position ── */}
      <div>
        <FieldLabel>Initial Position ({Math.round(sliderPos * 100)}%)</FieldLabel>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={sliderPos}
          onChange={e => patchBlock({ initialPosition: parseFloat(e.target.value) })}
          className="w-full accent-[var(--color-accent-primary-solid)]"
        />
        <p className="mt-0.5 text-[10px] text-fg-secondary">Where the divider starts when the reader opens the article</p>
      </div>

      {/* ── Width ── */}
      <div>
        <FieldLabel>Width</FieldLabel>
        <div className="flex gap-1">
          {WIDTH_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => patchBlock({ width: opt.value })}
              className={`rounded border px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                width === opt.value
                  ? 'border-accent-primary-solid bg-accent-primary-fill text-accent-primary-fg'
                  : 'border-line bg-canvas text-fg-secondary hover:bg-fill'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Aspect ratio ── */}
      <div>
        <FieldLabel>Aspect Ratio</FieldLabel>
        <select
          value={aspectRatio}
          onChange={e => patchBlock({ aspectRatio: e.target.value as WirePhotoAspectRatio })}
          className="w-full rounded border border-line bg-canvas px-2 py-1 text-xs text-fg focus:border-accent-primary-line focus:outline-none"
        >
          {RATIO_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.value === 'natural' ? 'Natural — preserve original' : opt.label}
            </option>
          ))}
        </select>
      </div>

    </div>
  )
}
